// IndexedDB에서 데이터 불러오기
export async function loadFromIndexedDB({
    dbName,
    storeName,
    saveId,
    requireIdMatch = false,
    confirmMsgFn,
    onRestore,
}) {
    try {
        const allData = await getAllFromIndexedDB({ dbName, storeName });

        if (!allData || allData.length === 0) {
            return;
        }

        const now = Date.now();
        const validData = allData.filter((data) => !data.expiresAt || data.expiresAt > now);

        if (validData.length === 0) {
            return;
        }

        let targetData = null;
        if (requireIdMatch) {
            targetData = validData.find((data) => data.id === saveId);

            if (!targetData) {
                return;
            }
        } else {
            targetData = validData.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt))[0];
        }

        if (confirmMsgFn(targetData)) {
            const restoredData = targetData.data ? decodeBase64(targetData.data) : targetData.data;
            onRestore(restoredData);
        }
        for (const data of allData) {
            await deleteFromIndexedDB({ dbName, storeName, id: data.id });
        }
    } catch (e) {
        console.error(e);
    }
}

// IndexedDB에서 모든 데이터 가져오기
export function getAllFromIndexedDB({ dbName, storeName }) {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName, 1);

        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(storeName)) {
                db.createObjectStore(storeName, { keyPath: "id" });
            }
        };

        request.onsuccess = (event) => {
            const db = event.target.result;

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                resolve([]);
                return;
            }

            const tx = db.transaction(storeName, "readonly");
            const store = tx.objectStore(storeName);
            const getAllRequest = store.getAll();

            getAllRequest.onsuccess = () => {
                db.close();
                resolve(getAllRequest.result);
            };

            getAllRequest.onerror = () => {
                db.close();
                reject(getAllRequest.error);
            };
        };

        request.onerror = (e) => {
            console.error(e);
            reject(e);
        };
    });
}

// IndexedDB에 데이터 저장
export function saveToIndexedDB({ dbName, storeName, data, saveId, EXPIRE_TIME_MS }) {
    return new Promise((resolve) => {
        const expiresAt = Date.now() + EXPIRE_TIME_MS;
        const id = saveId;
        const request = indexedDB.open(dbName, 1);

        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(storeName)) {
                db.createObjectStore(storeName, { keyPath: "id" });
            }
        };

        request.onsuccess = async (event) => {
            const db = event.target.result;

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                resolve();
                return;
            }

            try {
                await new Promise((resolve2) => {
                    const tx = db.transaction(storeName, "readwrite");
                    const store = tx.objectStore(storeName);

                    const plainData = JSON.parse(JSON.stringify(data));
                    const encodedData = encodeBase64 ? encodeBase64(plainData) : plainData;
                    const dataToSave = {
                        id,
                        data: encodedData,
                        savedAt: new Date().toISOString(),
                        expiresAt,
                        vulTitle: plainData.vulTitle,
                        rewardNm: plainData.rewardNm,
                    };

                    store.put(dataToSave);

                    tx.oncomplete = () => {
                        resolve2();
                    };

                    tx.onerror = (e) => {
                        console.error(e);
                        resolve2();
                    };
                });

                db.close();
            } catch (e) {
                console.error(e);
                db.close();
            }

            resolve();
        };

        request.onerror = (e) => {
            console.error(e);
            resolve();
        };
    });
}

// IndexedDB에서 특정 데이터 삭제
export function deleteFromIndexedDB({ dbName, storeName, id }) {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName, 1);

        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(storeName)) {
                db.createObjectStore(storeName, { keyPath: "id" });
            }
        };

        request.onsuccess = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                resolve();
                return;
            }
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            const deleteRequest = store.delete(id);

            deleteRequest.onsuccess = () => {
                db.close();
                resolve();
            };

            deleteRequest.onerror = () => {
                db.close();
                reject(deleteRequest.error);
            };
        };

        request.onerror = (e) => {
            console.error(e);
            reject(e);
        };
    });
}

// JSON 객체를 base64로 인코딩
export function encodeBase64(value) {
    try {
        const json = JSON.stringify(value);
        const bytes = new TextEncoder().encode(json);
        let binary = "";
        bytes.forEach((b) => {
            binary += String.fromCharCode(b);
        });
        return btoa(binary);
    } catch (e) {
        console.error(e);
        return null;
    }
}

// base64를 JSON 객체로 디코딩
export function decodeBase64(value) {
    if (!value) {
        return null;
    }
    try {
        const binary = atob(value);
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        const json = new TextDecoder().decode(bytes);
        return JSON.parse(json);
    } catch (e) {
        console.error(e);
        return null;
    }
}

// 데이터 비교(깊은 비교, HTML 필드 제외)
export function normalizeData(data) {
    if (!data) return data;
    const normalized = JSON.parse(JSON.stringify(data));
    Object.keys(normalized).forEach((key) => {
        if (key.endsWith("Html")) {
            delete normalized[key];
        }
    });
    return normalized;
}

export function deepEqual(obj1, obj2) {
    return JSON.stringify(normalizeData(obj1)) === JSON.stringify(normalizeData(obj2));
}

// 모든 IndexedDB 데이터베이스 삭제
export function deleteAllIndexedDBs() {
    if (!indexedDB?.databases) {
        return;
    }
    indexedDB
        .databases()
        .then((dbs) =>
            Promise.all(
                dbs
                    .map((db) => db?.name)
                    .filter(Boolean)
                    .filter((name) => name === "vulReport_db" || name === "vulPatch_db")
                    .map(
                        (name) =>
                            new Promise((resolve) => {
                                const req = indexedDB.deleteDatabase(name);
                                req.onsuccess = req.onerror = req.onblocked = resolve;
                            })
                    )
            )
        )
        .catch((e) => {
            console.error(e);
        });
}

// IndexedDB에서 만료된 데이터 삭제
export function deleteExpiredIndexedDBs({ targets }) {
    const now = Date.now();
    targets.forEach(({ dbName, storeName }) => {
        const request = indexedDB.open(dbName, 1);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(storeName)) {
                db.createObjectStore(storeName, { keyPath: "id" });
            }
        };

        request.onsuccess = (event) => {
            const db = event.target.result;

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                return;
            }

            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);

            store.getAll().onsuccess = (e) => {
                const records = e.target.result || [];
                records.forEach((record) => {
                    if (record && record.expiresAt && record.expiresAt <= now) {
                        store.delete(record.id);
                    }
                });
            };

            tx.oncomplete = () => db.close();
            tx.onerror = () => db.close();
        };
    });
}
